
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { Toaster } from "@/components/ui/sonner";
import { initializeOptimizations } from '@/utils/optimizeImages';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const location = useLocation();
  
  // Apply performance optimizations when the layout mounts
  useEffect(() => {
    // Initialize essential optimizations immediately
    initializeOptimizations();
    
    // Track performance metrics
    if (window.performance && 'PerformanceObserver' in window) {
      // Create a performance observer to monitor loading metrics
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.entryType === 'largest-contentful-paint') {
            console.log('LCP:', entry.startTime);
          }
        });
      });
      
      // Observe LCP performance entries
      observer.observe({ type: 'largest-contentful-paint', buffered: true });
      
      return () => {
        observer.disconnect();
      };
    }
    
    // Handle focus management for accessibility
    if (location.hash === '#top') {
      const mainContent = document.querySelector('main');
      if (mainContent) {
        mainContent.setAttribute('tabindex', '-1');
        mainContent.focus();
        mainContent.removeAttribute('tabindex');
      }
    }
  }, []);
  
  // Scroll to top when the route changes, but do it more efficiently
  useEffect(() => {
    if (location.hash === '') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    } else {
      const targetElement = document.querySelector(location.hash);
      if (targetElement) {
        targetElement.scrollIntoView();
      }
    }
  }, [location.pathname, location.hash]);

  return (
    <div className="flex flex-col min-h-screen">
      <Toaster position="top-right" />
      <Header />
      <main className="flex-grow">{children}</main>
      <Footer />
    </div>
  );
};

// Export memoized version to prevent unnecessary re-renders
export default React.memo(Layout);
